/**
 * The free-tier schedule gate: what occupies a slot, and when the wall fires.
 *
 * Kept as a standalone, dependency-free module ON PURPOSE:
 *   1. It is pure and therefore testable. The gate previously lived inline in
 *      UserMessageReducer, which imports the whole DB-service graph (and through it
 *      `appConfig`), so it could not be unit-tested at all — which is exactly why
 *      the bug below survived in production.
 *   2. It is the single source of truth for "is this message using a slot", so the
 *      gate and the scheduled-messages LIST cannot drift apart again.
 *
 * THE BUG THIS FIXES: the gate used to count every non-deleted doc in the
 * user-message store with NO status filter, while the list the user sees filters
 * `status: 'pending'` and drops campaign children. So we showed a paywall over an
 * empty list. Measured on prod the day it was found:
 *   - 2,787 free users sat AT THE WALL with ZERO pending messages — one FAILED
 *     message (the store loads status $in ['pending','failed']) consumed their
 *     only slot.
 *   - 1,585 more had their single free slot eaten by a failed message.
 *   - Campaign CHILD messages land in the same store (CampaignThunks, and the
 *     unbounded find({}) in ScheduledMessagesList), so merely opening a campaign
 *     view could instantly wall a free user.
 */

/**
 * The only fields the gate reads. Declared structurally so this module needs no
 * runtime import, but typed to EXACTLY match the real `UserMessage` schema so it
 * satisfies the constraint with no cast:
 *   - `status`  — MessageStatus literal union, widens to `string`
 *   - `deleted` — `z.boolean().optional()`
 *   - `campaignId` — `z.union([z.string(), z.number(), z.null()]).optional()`,
 *     hence `string | number | null | undefined` (a number is legal here; narrowing
 *     this to `string | null` breaks generic inference on GenericState<UserMessage>).
 */
export interface SlotCountableMessage {
  status?: string;
  deleted?: boolean;
  campaignId?: string | number | null;
}

/**
 * Entity-adapter shape of the user-message store, generic over the row type so the
 * real `GenericState<UserMessage>` (whose `entities` is RTK's `Dictionary<T>`, i.e.
 * `Record<string, T | undefined>`) is accepted with no cast and no `any`.
 */
export interface UserMessageEntityState<T extends SlotCountableMessage = SlotCountableMessage> {
  entities?: Record<string, T | undefined>;
}

/**
 * A message occupies a free-tier slot only if the user genuinely has it scheduled:
 * not deleted, still pending, and standalone (campaign children are not the user's
 * own scheduled messages and must never consume the free slot).
 */
export const occupiesScheduleSlot = <T extends SlotCountableMessage>(
  message: T | undefined,
): boolean =>
  message !== undefined
  && message.deleted !== true
  && message.status === 'pending'
  && !message.campaignId;

/** How many free-tier schedule slots the user is actually using. */
export const countScheduleSlotsUsed = <T extends SlotCountableMessage>(
  state: UserMessageEntityState<T> | undefined,
): number => {
  const entities = state?.entities;
  if (!entities) return 0;
  let used = 0;
  for (const key of Object.keys(entities)) {
    if (occupiesScheduleSlot(entities[key])) used++;
  }
  return used;
};

/**
 * Does the scheduling wall fire?
 *
 * `slotsUsed >= messageLimit` — the limit is a MAX concurrent count, so a free user
 * (limit 1) holding one pending message cannot add a second.
 *
 * `isEditingExistingMessage` — editing a message you already have must never wall
 * you; you are not consuming a new slot. (Paid plans have `messageLimit = Infinity`,
 * so this is always false for them.)
 */
export const isOverScheduleLimit = (
  slotsUsed: number,
  messageLimit: number,
  isEditingExistingMessage: boolean,
): boolean => slotsUsed >= messageLimit && !isEditingExistingMessage;

/* ------------------------------------------------------------------------- *
 * #131 T2 — the free-tier limit EXPERIMENT (control / 3-concurrent / 10-per-month)
 * ------------------------------------------------------------------------- */

/**
 * The limits the SERVER says this user is subject to.
 *
 * Server-assigned on purpose: the extension's paywall holdout hashes with djb2 while the
 * backend assigns with sha256, and they disagree — so the client cannot compute its own
 * arm. It reads this from `get-free-limit`.
 *
 * `null` means "no cap" on that axis (Infinity does not survive JSON).
 */
export interface FreeLimitPolicy {
  arm: string;
  /** Max concurrent PENDING standalone messages. null = no concurrent cap. */
  concurrentLimit: number | null;
  /** Max messages scheduled per calendar month (UTC). null = no monthly cap. */
  monthlyQuota: number | null;
  /** How many the user has already scheduled this calendar month. */
  monthlyUsed: number;
}

/**
 * The `control` ARM of the #131 T2 experiment: one concurrent pending message.
 *
 * NO LONGER THE DEFAULT. It is reachable only through the support-comp
 * `freeLimitArmOverride`, and is kept exported because the experiment's arms are
 * still a real vocabulary (analysis recomputes historical arms from userId).
 * Read `FREE_BASELINE_POLICY` below for what a free user is actually subject to.
 */
export const CONTROL_POLICY: FreeLimitPolicy = {
  arm: 'control',
  concurrentLimit: 1,
  monthlyQuota: null,
  monthlyUsed: 0,
};

/**
 * What a free user is ACTUALLY subject to, and the fallback whenever the server
 * payload has not arrived, failed, or is malformed.
 *
 * THE BUG THIS FIXES. #131 T2 concluded on 2026-07-22: `resolveFreeLimits`
 * (whatsapp-scheduler-backend `src/services/experiments/free-limit.ts`) stopped
 * hashing users into arms and now returns `FREE_DEFAULT_LIMITS` — 3 concurrent,
 * no monthly cap — to EVERY free user. The client never followed. It kept
 * falling back to the retired control arm's 1, so on a policy-fetch failure a
 * free user was walled two messages early, and the badge in the subscription bar
 * read "1 parallel message" while the paywall three inches away read "Free lets
 * you line up 3 at once". Both are the free limit; only one of them was true.
 *
 * "Fail closed" is still the rule — this is not a free upgrade. Three IS the free
 * plan. The backend's own fail-safe says so in as many words: "A bug here must
 * never hand out a MORE-restrictive limit than intended." Defaulting to 1 was
 * failing closed against a limit that no longer exists.
 *
 * Kept in step with the backend by VALUE, not by import (the two repos share no
 * build). If the free baseline moves, it moves in `free-limit.ts` first and here
 * second — `free-limit-gate.test.ts` names that pairing so the next reader knows.
 */
export const FREE_BASELINE_POLICY: FreeLimitPolicy = {
  arm: 'three_concurrent',
  concurrentLimit: 3,
  monthlyQuota: null,
  monthlyUsed: 0,
};

/** Which cap fired. Reported on `scheduling_wall_hit` as `limit_reason`. */
export type WallReason = 'concurrent' | 'monthly';

export interface ScheduleGate {
  blocked: boolean;
  reason: WallReason | null;
  arm: string;
}

/**
 * The whole gate, in one pure function.
 *
 * Order matters: paid and editing short-circuit first (they can never be walled), then
 * the concurrent cap, then the monthly quota. A user can only be blocked by one reason,
 * and we report the one that actually fired so the funnel can tell a "plan-ahead" wall
 * apart from a "volume" wall.
 */
/**
 * Is this payload usable as a policy?
 *
 * We must NOT use `??` to default the caps: `null` is a MEANINGFUL value here (it means
 * "no cap"), and `null ?? 1` collapses it back to 1 — which would silently cap the
 * ten_per_month arm at one concurrent message and quietly destroy the experiment.
 * So we validate the SHAPE up-front and only then read the caps verbatim.
 */
/** A cap is either a number or an explicit null ("no cap"). Nothing else is acceptable. */
const isCap = (v: unknown): v is number | null => v === null || typeof v === 'number';

/**
 * Validate the VALUE TYPES, not just key presence.
 *
 * `'concurrentLimit' in p` is true for `{ concurrentLimit: undefined }`, and the cap checks
 * in evaluateScheduleGate skip undefined — which would mean NO CAP AT ALL. That is the exact
 * inverse of the `??`-collapses-null bug this guard exists to prevent.
 *
 * No type assertions: `typeof p === 'object' && p !== null` narrows to object, and the `in`
 * operator then narrows the property to `unknown`, which isCap() refines.
 */
export const isUsablePolicy = (p: unknown): p is FreeLimitPolicy => {
  if (typeof p !== 'object' || p === null) return false;
  if (!('concurrentLimit' in p) || !('monthlyQuota' in p)) return false;
  return isCap(p.concurrentLimit) && isCap(p.monthlyQuota);
};

export const evaluateScheduleGate = (input: {
  slotsUsed: number;
  isEditingExistingMessage: boolean;
  /** Paid plans have messageLimit === Infinity and are never subject to the free caps. */
  paidUnlimited: boolean;
  policy: FreeLimitPolicy;
}): ScheduleGate => {
  const { slotsUsed, isEditingExistingMessage, paidUnlimited } = input;

  // FAIL CLOSED: anything missing or malformed behaves exactly like the free plan does
  // today — which since 2026-07-22 is the 3-concurrent baseline, not the retired
  // control arm's 1. A broken payload must hand out neither a free upgrade nor a
  // limit stricter than the one the product's own copy promises.
  const policy: FreeLimitPolicy = isUsablePolicy(input.policy) ? input.policy : FREE_BASELINE_POLICY;
  const arm = policy.arm ?? FREE_BASELINE_POLICY.arm;

  if (paidUnlimited) return { blocked: false, reason: null, arm };
  if (isEditingExistingMessage) return { blocked: false, reason: null, arm };

  // null = no concurrent cap (the ten_per_month arm).
  const { concurrentLimit } = policy;
  if (concurrentLimit !== null && concurrentLimit !== undefined && slotsUsed >= concurrentLimit) {
    return { blocked: true, reason: 'concurrent', arm };
  }

  // null = no monthly cap (control and three_concurrent).
  const { monthlyQuota } = policy;
  const monthlyUsed = policy.monthlyUsed ?? 0;
  if (monthlyQuota !== null && monthlyQuota !== undefined && monthlyUsed >= monthlyQuota) {
    return { blocked: true, reason: 'monthly', arm };
  }

  return { blocked: false, reason: null, arm };
};

export interface BulkScheduleGate {
  blocked: boolean;
  /** The cap that refuses the batch (the tighter one when both would). */
  reason: WallReason | null;
  /** How many more messages the plan allows right now; null = no cap on either axis. */
  remaining: number | null;
}

/**
 * The same gate, asked for N new messages at once (FE#444 "Schedule again").
 *
 * `evaluateScheduleGate` answers "may I add ONE more?" (blocked when slotsUsed >= limit). A bulk
 * action must ask "may I add N?": slotsUsed + N must stay within the concurrent cap AND
 * monthlyUsed + N within the monthly quota. The batch is refused whole rather than trimmed — the
 * caller says how many slots are left and lets the user choose which ones to run.
 * Same fail-closed policy handling as the single gate.
 */
export const evaluateBulkScheduleGate = (input: {
  slotsUsed: number;
  count: number;
  paidUnlimited: boolean;
  policy: FreeLimitPolicy;
}): BulkScheduleGate => {
  if (input.paidUnlimited) return { blocked: false, reason: null, remaining: null };

  const policy: FreeLimitPolicy = isUsablePolicy(input.policy) ? input.policy : FREE_BASELINE_POLICY;

  const concurrentLeft = policy.concurrentLimit === null || policy.concurrentLimit === undefined
    ? null
    : Math.max(policy.concurrentLimit - input.slotsUsed, 0);
  const monthlyLeft = policy.monthlyQuota === null || policy.monthlyQuota === undefined
    ? null
    : Math.max(policy.monthlyQuota - (policy.monthlyUsed ?? 0), 0);

  if (concurrentLeft === null && monthlyLeft === null) return { blocked: false, reason: null, remaining: null };

  // The tighter cap decides; on a tie the concurrent cap is the one reported.
  const monthlyIsTighter = monthlyLeft !== null && (concurrentLeft === null || monthlyLeft < concurrentLeft);
  const remaining = monthlyIsTighter ? monthlyLeft : concurrentLeft;
  const reason: WallReason = monthlyIsTighter ? 'monthly' : 'concurrent';
  const blocked = remaining !== null && input.count > remaining;
  return { blocked, reason: blocked ? reason : null, remaining };
};

export interface ScheduleWallState {
  /** True once we KNOW enough to decide the gate (identity resolved AND policy fetched). */
  policySettled: boolean;
  /** Render nothing: the composer is open but the gate is not yet decidable. */
  awaitingPolicy: boolean;
  /** Show the wall: the user is over their cap and we are sure of it. */
  isOverLimit: boolean;
}

/**
 * Derive whether to show the composer, the wall, or nothing — in one pure, testable place.
 *
 * IDENTITY GATE (the #131 T2 rig-caught race): the policy does NOT count as settled until
 * `userId` has resolved. The earlier `!userId || freeLimitSettled` treated a not-yet-loaded
 * user as settled, so the wall could fire on the fallback policy before identity arrived — which
 * (a) locked the wrong T1 paywall variant (a holdout user saw the feature modal) and (b) let
 * a T2 treatment user be walled at the control limit and mis-tagged 'control' in
 * scheduling_wall_hit. Until identity is known we stay in `awaitingPolicy` and render nothing.
 *
 * Paid users never consult the policy (they are never walled), so they are always settled and
 * never wait — identity-load timing must not make a paid user's composer flicker.
 */
export const deriveScheduleWallState = (input: {
  open: boolean;
  /** userId has resolved. */
  hasIdentity: boolean;
  /** the free-limit policy fetch has settled (ready or error). */
  freeLimitSettled: boolean;
  paidUnlimited: boolean;
  /** evaluateScheduleGate(...).blocked */
  gateBlocked: boolean;
  /**
   * FE#167 — the SUBSCRIPTION entitlement has not resolved yet
   * (scheduleEntitlementSelector === 'unknown'). Orthogonal to `freeLimitSettled`,
   * which is about the free-limit POLICY: a paying customer's policy settles instantly
   * while their subscription is still in flight, and that is precisely the window in
   * which prod walled Pedro.
   *
   * Treated as "not settled" rather than as a paid/free verdict on purpose: we may not
   * accuse a paying customer of being free, and we may not hand out the composer to
   * someone who may be over their cap. HOLD is the only honest state, and it costs
   * nothing — the composer opens the moment the subscription lands, with no reload
   * (`awaitingPolicy` is derived, not latched).
   */
  entitlementUnknown?: boolean;
}): ScheduleWallState => {
  const { open, hasIdentity, freeLimitSettled, paidUnlimited, gateBlocked } = input;
  const entitlementUnknown = input.entitlementUnknown === true;
  const policySettled =
    paidUnlimited || (hasIdentity && freeLimitSettled && !entitlementUnknown);
  const awaitingPolicy = open && !paidUnlimited && !policySettled;
  const isOverLimit = gateBlocked && policySettled;
  return { policySettled, awaitingPolicy, isOverLimit };
};

export interface BulkScheduleVerdict {
  /** The batch does not fit and we are sure of it — refuse it. */
  refused: boolean;
  /** Not decidable yet (identity, policy or subscription still loading) — do neither. */
  awaiting: boolean;
  remaining: number | null;
  reason: WallReason | null;
}

/**
 * `evaluateBulkScheduleGate` + the same "do we know enough to decide?" rule the composer uses
 * (`deriveScheduleWallState`), as one pure answer for both the dialog (to disable Schedule) and the
 * thunk (to refuse). Until identity, the policy and the subscription are known the verdict is
 * `awaiting`, so nobody is refused against a plan we have not read and nobody is let through on one.
 */
export const deriveBulkScheduleVerdict = (input: {
  hasIdentity: boolean;
  freeLimitSettled: boolean;
  entitlementUnknown: boolean;
  slotsUsed: number;
  count: number;
  paidUnlimited: boolean;
  policy: FreeLimitPolicy;
}): BulkScheduleVerdict => {
  const gate = evaluateBulkScheduleGate(input);
  const wall = deriveScheduleWallState({
    open: true,
    hasIdentity: input.hasIdentity,
    freeLimitSettled: input.freeLimitSettled,
    paidUnlimited: input.paidUnlimited,
    gateBlocked: gate.blocked,
    entitlementUnknown: input.entitlementUnknown,
  });
  return {
    refused: wall.isOverLimit,
    awaiting: wall.awaitingPolicy,
    remaining: gate.remaining,
    reason: wall.isOverLimit ? gate.reason : null,
  };
};

